/**
 * Automatic page view capture for FirstDistro SDK
 *
 * Patches the History API (pushState/replaceState) and listens for popstate and
 * hashchange to detect SPA route changes (incl. hash routers), emitting one
 * $pageview per real navigation. Capture is identity-gated (only fires once
 * identity is set — a userId or accountId) and deduped by path + hash so
 * replaceState loops and query-only changes don't double-count.
 *
 * Query strings are stripped from the captured `url` and `referrer` by default
 * (PII/secret safety); vendors opt back in to named params via an allowlist,
 * and a code-install `maskUrl` hook can further mask or drop a capture.
 */
import { scrubUrl } from './url-scrub';

/**
 * Page view payload attached to the $pageview event properties.
 */
export interface PageviewProperties {
  pathname: string;
  url: string;
  referrer?: string;
}

export interface PageviewCaptureOptions {
  /** Returns true once identity is set (userId or accountId) — the capture gate. */
  hasIdentity: () => boolean;
  /** Emits a $pageview with the given properties. */
  onCapture: (properties: PageviewProperties) => void;
  /**
   * Allowlist of query-param names to keep on the captured `url`/`referrer`.
   * Default (undefined/empty) strips all query params.
   */
  allowedQueryParams?: string[];
  /**
   * Code-install hook run AFTER the default strip. Returning `null` drops the
   * $pageview capture entirely; returning a string replaces the captured url.
   */
  maskUrl?: (url: string) => string | null;
}

type HistoryMethod = (this: History, data: any, unused: string, url?: string | URL | null) => void;

type PatchedHistoryMethod = HistoryMethod & { __fdNative?: HistoryMethod };

/**
 * PageviewCapture
 *
 * Lifecycle: construct -> start() -> maybeCapture()/navigation events -> stop().
 * All methods are SSR-safe (no-op when window is undefined).
 */
export class PageviewCapture {
  private readonly hasIdentity: () => boolean;
  private readonly onCapture: (properties: PageviewProperties) => void;
  private readonly allowedQueryParams?: string[];
  private readonly maskUrl?: (url: string) => string | null;

  private started = false;
  private lastKey: string | null = null;

  private originalPushState: HistoryMethod | null = null;
  private originalReplaceState: HistoryMethod | null = null;
  private pushWrapper: PatchedHistoryMethod | null = null;
  private replaceWrapper: PatchedHistoryMethod | null = null;
  private popstateHandler: (() => void) | null = null;
  private hashchangeHandler: (() => void) | null = null;

  constructor(options: PageviewCaptureOptions) {
    this.hasIdentity = options.hasIdentity;
    this.onCapture = options.onCapture;
    this.allowedQueryParams = options.allowedQueryParams;
    this.maskUrl = options.maskUrl;
  }

  /**
   * Patch the History API and subscribe to navigation events.
   */
  start(): void {
    if (typeof window === 'undefined') return;
    if (this.started) return;
    this.started = true;

    const history = window.history;

    const handleNavigation = () => this.maybeCapture();

    const currentPush = history.pushState as PatchedHistoryMethod;
    this.originalPushState = currentPush.__fdNative ?? currentPush;
    const pushWrapper = ((...args: Parameters<HistoryMethod>) => {
      this.originalPushState?.apply(history, args);
      handleNavigation();
    }) as PatchedHistoryMethod;
    pushWrapper.__fdNative = this.originalPushState ?? undefined;
    history.pushState = pushWrapper;
    this.pushWrapper = pushWrapper;

    const currentReplace = history.replaceState as PatchedHistoryMethod;
    this.originalReplaceState = currentReplace.__fdNative ?? currentReplace;
    const replaceWrapper = ((...args: Parameters<HistoryMethod>) => {
      this.originalReplaceState?.apply(history, args);
      handleNavigation();
    }) as PatchedHistoryMethod;
    replaceWrapper.__fdNative = this.originalReplaceState ?? undefined;
    history.replaceState = replaceWrapper;
    this.replaceWrapper = replaceWrapper;

    this.popstateHandler = handleNavigation;
    window.addEventListener('popstate', this.popstateHandler);

    // Hash routers navigate via location.hash, which fires `hashchange`
    // (not popstate) and leaves pathname unchanged.
    this.hashchangeHandler = handleNavigation;
    window.addEventListener('hashchange', this.hashchangeHandler);
  }

  /**
   * Restore the History API and remove listeners.
   */
  stop(): void {
    if (typeof window === 'undefined') return;
    if (!this.started) return;
    this.started = false;

    const history = window.history;

    if (history.pushState === this.pushWrapper && this.originalPushState) {
      history.pushState = this.originalPushState;
    }
    this.pushWrapper = null;
    this.originalPushState = null;

    if (history.replaceState === this.replaceWrapper && this.originalReplaceState) {
      history.replaceState = this.originalReplaceState;
    }
    this.replaceWrapper = null;
    this.originalReplaceState = null;
    if (this.popstateHandler) {
      window.removeEventListener('popstate', this.popstateHandler);
      this.popstateHandler = null;
    }
    if (this.hashchangeHandler) {
      window.removeEventListener('hashchange', this.hashchangeHandler);
      this.hashchangeHandler = null;
    }
  }

  /**
   * Capture a $pageview if identity is set and the path or hash changed.
   * Called on initial identity, and on every SPA navigation.
   */
  maybeCapture(): void {
    if (typeof window === 'undefined') return;
    if (!this.hasIdentity()) return;

    const pathname = window.location.pathname;

    // Dedupe on path + hash: supports hash routers while suppressing
    // replaceState loops and query-only churn (search is excluded on purpose).
    const key = pathname + window.location.hash;
    if (key === this.lastKey) return;
    this.lastKey = key;

    let url = scrubUrl(window.location.href, this.allowedQueryParams);
    if (this.maskUrl) {
      const masked = this.maskUrl(url);
      if (masked === null) return;
      url = masked;
    }

    const referrer = document.referrer
      ? scrubUrl(document.referrer, this.allowedQueryParams)
      : undefined;

    this.onCapture({ pathname, url, referrer });
  }

  /**
   * Clear dedupe state (e.g. after reset()), so the next identified
   * navigation re-captures even if the path + hash is unchanged.
   */
  resetState(): void {
    this.lastKey = null;
  }
}
